/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/contextAccount.scss';

import React, {useContext, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {UserRole} from '../../../util/interfaces';

import {setElementHighlight} from '../../../util/utils';

import {AppContext} from '../../misc/appContext';

import Account from '../../../models/accountModel';

import {DVRDGroupedSelect, GroupedSelectItem, GroupedSelectRef} from '@dvrd/dvr-controls';

interface Props {
    onChangeAccount: (id: string) => void;
}

export default function ContextAccount(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {accounts, account} = context.accountContext;
    const {onChangeAccount} = props;
    const [ownedAccounts, accessAccounts]: [Array<Account>, Array<Account>] = useMemo(() => {
        const owned: Array<Account> = [];
        const access: Array<Account> = [];
        for (const account of accounts) {
            if (account.userRole === UserRole.OWNER) owned.push(account);
            else access.push(account);
        }
        return [owned, access];
    }, [accounts]);
    const items: Array<GroupedSelectItem> = useMemo(() => {
        let items: Array<GroupedSelectItem> = [];
        if (accessAccounts.length) {
            items.push({
                label: t('account.ownAccounts', 'Eigen accounts'),
                children: ownedAccounts
                    .map((account: Account) => ({
                        label: account.labelWithSellerID,
                        value: account.id
                    }))
                    .concat({value: '', label: t('account.addSalesAccount', '+ Verkoopaccount')})
            });
            items.push({
                label: t('account.otherAccounts', 'Andere accounts'),
                children: accessAccounts.map((account: Account) => ({
                    label: account.labelWithSellerID,
                    value: account.id
                }))
            });
        } else
            items = ownedAccounts
                .map((account: Account) => ({
                    label: account.labelWithSellerID,
                    value: account.id
                }))
                .concat({value: '', label: t('account.addSalesAccount', '+ Verkoopaccount')});
        return items;
    }, [ownedAccounts, accessAccounts, t]);
    const rootRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<GroupedSelectRef>(null);

    function onClickRoot(evt: React.MouseEvent) {
        evt.stopPropagation();
        setElementHighlight('div.account-select', false);
        selectRef.current?.toggle();
    }

    function renderAccountsContainer() {
        return (
            <DVRDGroupedSelect
                className='grouped-account-select'
                items={items}
                onChange={onChangeAccount}
                value={account?.id ?? ''}
                ref={selectRef}
                maxItemsHeight='70dvh'
                label={t('account.salesAccount', 'Verkoopaccount:')}
            />
        );
    }

    return (
        <div className='context-account' onClick={onClickRoot} ref={rootRef}>
            {renderAccountsContainer()}
            <p className='message-container'>
                {t('account.addExtraHint', 'Klik hier om een extra account toe te voegen.')}
            </p>
        </div>
    );
}
